import Color from "color";

export type ShadowElevation = "small" | "medium" | "large";

export type ShadowLayer = {
  x: number;
  y: number;
  blur: number;
  spread: number;
  opacity: number;
};

/**
 * Locked elevation layers (geometry + opacity).
 * Snapshot from the shadow palette playground — do not regenerate at runtime.
 */
export const ELEVATION_LAYERS: Record<ShadowElevation, readonly ShadowLayer[]> =
  {
    small: [
      { x: -0.2, y: -0.2, blur: 0.4, spread: 0.4, opacity: 0.21 },
      { x: 0.2, y: 0.5, blur: 0.6, spread: 0, opacity: 0.2 },
      { x: 0.3, y: 0.9, blur: 1.1, spread: -0.7, opacity: 0.2 },
      { x: 0.8, y: 2.1, blur: 2.5, spread: -1.4, opacity: 0.2 },
    ],
    medium: [
      { x: -0.2, y: -0.2, blur: 0.4, spread: 0.4, opacity: 0.21 },
      { x: 0, y: 0.5, blur: 0.6, spread: 0, opacity: 0.21 },
      { x: 0.6, y: 1.4, blur: 1.7, spread: -0.8, opacity: 0.21 },
      { x: 1.3, y: 3.3, blur: 4, spread: -1.7, opacity: 0.21 },
      { x: 3.2, y: 8, blur: 9.7, spread: -2.5, opacity: 0.21 },
    ],
    large: [
      { x: -0.2, y: -0.2, blur: 0.4, spread: 0.4, opacity: 0.21 },
      { x: 0.2, y: 0.5, blur: 0.6, spread: 0, opacity: 0.2 },
      { x: 0.7, y: 1.7, blur: 2.1, spread: -0.4, opacity: 0.2 },
      { x: 1.2, y: 2.9, blur: 3.5, spread: -0.7, opacity: 0.2 },
      { x: 1.9, y: 4.7, blur: 5.7, spread: -1.1, opacity: 0.2 },
      { x: 2.9, y: 7.3, blur: 8.8, spread: -1.4, opacity: 0.2 },
      { x: 4.5, y: 11.3, blur: 13.7, spread: -1.8, opacity: 0.2 },
      { x: 6.8, y: 17, blur: 20.6, spread: -2.1, opacity: 0.2 },
      { x: 10, y: 24.9, blur: 30.2, spread: -2.5, opacity: 0.2 },
    ],
  };

/** Default shadow tint for light page surfaces (`#f9f9f9`). */
export const DEFAULT_SHADOW_HSL = "0deg 0% 75%";

/** Oomph used when deriving an `against` tint (matches locked palette). */
const TINT_OOMPH = 0.1;

function normalize(
  number: number,
  currentScaleMin: number,
  currentScaleMax: number,
  newScaleMin = 0,
  newScaleMax = 1,
): number {
  const standardNormalization =
    (number - currentScaleMin) / (currentScaleMax - currentScaleMin);
  return (newScaleMax - newScaleMin) * standardNormalization + newScaleMin;
}

function roundTo(number: number, places = 0): number {
  return Math.round(number * 10 ** places) / 10 ** places;
}

function clamp(val: number, min = 0, max = 1): number {
  return Math.max(min, Math.min(max, val));
}

function layersToBoxShadow(
  layers: readonly ShadowLayer[],
  shadowHsl: string,
): string {
  return layers
    .map((layer) => {
      const spread = layer.spread !== 0 ? `${layer.spread}px ` : "";
      return `${layer.x}px ${layer.y}px ${layer.blur}px ${spread}hsl(${shadowHsl} / ${layer.opacity})`;
    })
    .join(", ");
}

/** Precomputed default shadows (no `against`). */
export const ELEVATION_SHADOWS: Record<ShadowElevation, string> = {
  small: layersToBoxShadow(ELEVATION_LAYERS.small, DEFAULT_SHADOW_HSL),
  medium: layersToBoxShadow(ELEVATION_LAYERS.medium, DEFAULT_SHADOW_HSL),
  large: layersToBoxShadow(ELEVATION_LAYERS.large, DEFAULT_SHADOW_HSL),
};

/**
 * Derive a Comeau-style shadow tint from the surface the element sits on.
 * Returns space-separated HSL for use in `hsl(… / opacity)`.
 */
export function shadowColorFromBackground(color: string): string {
  const hsl = Color(color).hsl().object();
  const hue = hsl.h ?? 0;
  let sat = hsl.s ?? 0;
  let lit = hsl.l ?? 0;

  const maxLightness = normalize(TINT_OOMPH, 0, 1, 85, 50);
  const saturationEnhancement = normalize(lit, 50, 100, 1, 0.25);
  sat = Math.round(clamp(sat * saturationEnhancement, 0, 100));
  lit = Math.round(clamp(normalize(lit, 0, 100, 0, maxLightness) - 5, 0, 100));

  return `${roundTo(hue, 1)}deg ${sat}% ${lit}%`;
}

export type BoxShadowForOptions = {
  elevation: ShadowElevation;
  /** Surface colour the element sits on (CSS colour). */
  against?: string;
};

/**
 * Full `box-shadow` CSS value for an elevation.
 * Defaults use precomputed strings; `against` only retints frozen layers.
 */
export function boxShadowFor(options: BoxShadowForOptions): string {
  if (!options.against) {
    return ELEVATION_SHADOWS[options.elevation];
  }
  return layersToBoxShadow(
    ELEVATION_LAYERS[options.elevation],
    shadowColorFromBackground(options.against),
  );
}
